import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import {
  RentComplex,
  RentComplexProps,
} from '@features/effector-form/controls/rent-complex-controls/types';
import { withControl } from '@features/effector-form/hoc/withControl';
import { RentComplexesControlGate, rentComplexesQuery } from '../model';

export const RentComplexesControl = withControl<RentComplexProps>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    cities = [],
    isError,
    excludeOptions = [],
  }) => {
    useGate(RentComplexesControlGate, { cities });

    const { data, pending } = useUnit(rentComplexesQuery);

    const handleFilterOptions = (
      opts: RentComplex[],
      { inputValue }: { inputValue: string }
    ) => {
      const currentOptions = excludeOptions.length
        ? opts.reduce(
            (acc: RentComplex[], option) =>
              !excludeOptions.includes(option.id) ? [...acc, option] : acc,
            []
          )
        : opts;

      if (inputValue) {
        return currentOptions.filter((opt) =>
          opt?.label?.toUpperCase().includes(inputValue.toUpperCase())
        );
      }

      return currentOptions;
    };

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          filterOptions={handleFilterOptions}
          multiple
          loading={pending}
          value={!data ? [] : data.filter((item) => value.indexOf(item.id) > -1)}
          options={data || []}
          onChange={(_: unknown, newValue: any) => {
            setValue(newValue.map((item: RentComplex) => item.id));
          }}
          ListboxComponent={Listbox}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                readOnly,
                error: isError,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
